import React from 'react';
import { RATING_BANDS, colorsFor } from '../shared/sentimentBands';
import { AXIS_ENDS, BAR_NOTE } from '../shared/bucketCrowding';
import { HIGHLIGHT_COLORS } from './sectorChartShared';

/**
 * Draws the small visual on a mobile Brief card (issue #380) from the `{ kind, ... }`
 * description built in `src/utils/briefVisuals.js`.
 *
 * Plain HTML boxes sized in percentages, so every visual reflows to whatever width the
 * card has — no chart library, no scaled viewBox (which shrinks text at phone width).
 * Numbers and names are TEXT in the card's ink; colour only fills a mark. Every mark is
 * named by a word next to it, so nothing on a card needs explaining elsewhere.
 */

// Same blue / orange as the desktop "Asset classes" tab: bought-hard is not "good".
const [INFLOW, OUTFLOW] = HIGHLIGHT_COLORS;
// The movers' green / red: here up is simply up.
const UP = '#059669';
const DOWN = '#e11d48';

// Band widths come from the ONE band table; the last band is open-ended, so it ends at 100.
const BANDS = RATING_BANDS.map((band, index) => {
  const from = index === 0 ? 0 : RATING_BANDS[index - 1].maxExclusive;
  return { rating: band.rating, width: Math.min(band.maxExclusive, 100) - from };
});

const money = (n) => `$${n >= 100 ? Math.round(n).toLocaleString('en-US') : n.toFixed(2)}`;
const count = (n) => Math.round(n).toLocaleString('en-US');
// keeps a marker at 0 or 100 wholly inside its track instead of half off the card's edge
const at = (percent) => `min(max(${percent}%, 2px), calc(100% - 2px))`;
const signedPct = (v) => `${v > 0 ? '+' : ''}${v.toFixed(2)}%`;

/** The five rating bands as one track, the band holding `score` at full strength. */
const BandTrack = ({ score, rating, previous, slim = false }) => (
  <div className={`brief-visual-bands${slim ? ' slim' : ''}`}>
    <div className="brief-visual-band-row">
      {BANDS.map((band) => (
        <span
          key={band.rating}
          style={{
            flex: band.width,
            background: colorsFor(band.rating).bar,
            opacity: band.rating === rating ? 1 : 0.25,
          }}
        />
      ))}
    </div>
    {previous && <span className="brief-visual-ring" style={{ left: at(previous.score) }} />}
    <span className="brief-visual-tick" style={{ left: at(score) }} />
  </div>
);

const SentimentMeter = ({ visual, isExpanded }) => (
  <>
    <BandTrack score={visual.score} rating={visual.rating} previous={visual.previous} />
    <div className="brief-visual-ends">
      <span>Extreme fear</span>
      <span>Extreme greed</span>
    </div>
    {visual.previous && (
      <div className="brief-visual-note">
        <span className="brief-visual-ring inline" /> {Math.round(visual.previous.score)} {visual.previous.label}
      </div>
    )}
    {!isExpanded && visual.indicators.length > 0 && (
      <div className="brief-visual-note">Tap for the {visual.indicators.length} indicators behind it</div>
    )}
    {isExpanded && visual.indicators.map((row) => (
      <div key={row.key} className="brief-visual-indicator">
        <div className="brief-visual-ends ink">
          <span>{row.label}</span>
          <span>{row.rating}</span>
        </div>
        <BandTrack score={row.score} rating={row.rating} slim />
      </div>
    ))}
  </>
);

const SplitBar = ({ visual }) => (
  <>
    <div className="brief-visual-split">
      <span style={{ flex: visual.up, background: UP }} />
      <span style={{ flex: visual.down, background: DOWN }} />
    </div>
    <div className="brief-visual-ends ink">
      <span>Up {count(visual.up)}</span>
      <span>Down {count(visual.down)}</span>
    </div>
  </>
);

const RangeBar = ({ visual }) => (
  <>
    <div className="brief-visual-range">
      <span className="brief-visual-tick" style={{ left: at(visual.position * 100) }} />
    </div>
    <div className="brief-visual-ends">
      <span>52-week low {money(visual.low)}</span>
      <span>high {money(visual.high)}</span>
    </div>
  </>
);

const DivergingBars = ({ visual, isExpanded }) => (
  <>
    <div className="brief-visual-ends">
      <span>← {AXIS_ENDS.sold}</span>
      <span>{AXIS_ENDS.bought} →</span>
    </div>
    {visual.rows.map((row) => (
      <div key={row.key}>
        <div className="brief-visual-ends ink">
          <span>{row.label}</span>
          <span>{signedPct(row.movePct)}</span>
        </div>
        <div className="brief-visual-diverging-track">
          <span className="brief-visual-axis" />
          <span
            className="brief-visual-diverging-bar"
            style={{
              [row.positive ? 'left' : 'right']: '50%',
              width: `${row.share * 50}%`,
              background: row.positive ? INFLOW : OUTFLOW,
              borderRadius: row.positive ? '0 4px 4px 0' : '4px 0 0 4px',
            }}
          />
        </div>
      </div>
    ))}
    {isExpanded && <div className="brief-visual-note">{BAR_NOTE}</div>}
  </>
);

// Named groups as one bar (issue #387). Every segment is named in the legend with its count —
// a zero-count group is listed (so "0 lowered" is said) but draws no width.
const StackedBar = ({ visual }) => (
  <>
    <div className="brief-visual-split">
      {visual.segments.map((s) => s.count > 0 && (
        <span key={s.key} style={{ flex: s.count, background: s.color }} />
      ))}
    </div>
    <div className="brief-visual-legend">
      {visual.segments.map((s) => (
        <span key={s.key}>
          <i style={{ background: s.color }} />
          {count(s.count)} {s.label}
        </span>
      ))}
    </div>
  </>
);

const KINDS = {
  sentimentMeter: SentimentMeter,
  splitBar: SplitBar,
  rangeBar: RangeBar,
  divergingBars: DivergingBars,
  stackedBar: StackedBar,
};

const BriefVisual = ({ visual, isExpanded = false }) => {
  const Kind = visual && Object.hasOwn(KINDS, visual.kind) ? KINDS[visual.kind] : null;
  if (!Kind) return null;
  return (
    <div className="brief-visual">
      <Kind visual={visual} isExpanded={isExpanded} />
    </div>
  );
};

export default BriefVisual;
